<template>
  <div class="mod-overview">
    <div
      class="d-flex flex-column bg-transparent container-fluid justify-content-between content"
    >
      <h1 class="small-headline">Comments Moderation</h1>
      <p class="m-0">Click the dataset-id if you want to be redirected to the associated overview Page.</p>

      <div class="card-header d-flex">
        <ul class="mod-nav-tabs nav nav-tabs card-header-tabs d-flex w-100 text-center">
            <li class="nav-item w-50" role="tab" :aria-controls="'unpublished'"
            :aria-selected="activeTab === 'unpublished'" aria-label="unpublished comments">
                <a class="nav-link" :class="{active: activeTab === 'unpublished'}" href="#" @click.prevent="activeTab = 'unpublished'">View Unpublished Comments</a>
            </li>
            <li class="nav-item w-50" role="tab" :aria-controls="'published'"
            :aria-selected="activeTab === 'published'" aria-label="published comments">
                <a class="nav-link" :class="{active: activeTab === 'published'}" href="#" @click.prevent="activeTab = 'published'">View Published Comments</a>
            </li>
        </ul>
    </div>
      <table v-if="allComments.length > 0">
        <thead>
          <tr>
            <th>{{ $t("message.metadata.dataset") }} - ID</th>
            <th>Last Update</th>
            <th>Comment</th>
            <th>Reported</th>
            <th>{{ $t("message.dataupload.menu.actions") }}</th>
          </tr>
        </thead>
        <CommentEntry v-for="(comment, index) in allComments" :key="index" 
          :comment="comment"
          :mode="activeTab"
          :formatDatetime="formatDatetime"
          :handlePublish="handlePublish"
          :handleUnpublish="handleUnpublish"
          :handleDelete="handleDelete"
          :handleWithdrowingReport="handleWithdrowingReport"
        />
      </table>
      <p v-else class="mt-5 text-center">
        No comments to show here.
      </p>
      <Pagination class="mt-2" :itemsCount="commentsCount" :itemsPerPage="itemsPerPage" :getPage="pageNum" :getPageCount="pageCount" @setPageLimit="setPageLimit" @changePageTo="changePageTo"/>
    </div>
  </div>
</template>

<script setup>
import { useStore } from "vuex";
import { ref, computed, onMounted, watch } from "vue";
import { getCurrentInstance } from "vue";
import { formatDatetime } from "@/utils/utils";
import {
  getAllComments,
  publishComment,
  deleteComment,
  unpublishComment,
  withdrawReport
} from '../services/commentsAndLikesAPIService';
import CommentEntry from "./CommentEntry.vue";
import Pagination from '../components/datasetDetails/datasetDetailsCommentsAndLikes/CustomPagination.vue'

let env = getCurrentInstance().appContext.app.config.globalProperties.$env;
const baseUrl = env.content.datasetDetails.ratingServiceUrl;
const store = useStore();
const activeTab = ref('unpublished')
const getUserData = computed(() => store.getters["auth/getUserData"]);
const authToken = computed(() => getUserData.value?.rtpToken);
const allComments = ref([]);
const commentsCount = ref(0);
const itemsPerPage = ref(10);
const pageNum = ref(1);
const pageCount = ref(1);
const offset = ref(0);

onMounted(async () => {
  getComments()
});

watch(authToken, (newVal) => {
  if (newVal) {
    getComments();
  }
});

watch(activeTab, (newVal) => {
  if (newVal) {
    allComments.value = []
    pageNum.value = 0
    offset.value = 0
    getComments()
  }
});

const setPageLimit = (value) => {
  itemsPerPage.value = value;
  getComments()
}

const changePageTo = (value) => {
  pageNum.value = value;
  offset.value = (pageNum.value -1) * itemsPerPage.value
  getComments()
}

const getComments = () => {
  allComments.value = []
  getAllComments(baseUrl, authToken.value, activeTab.value, itemsPerPage.value, offset.value)
    .then((response) => {
      const responseData = response.data.data;
      commentsCount.value = response.data.total;
      if(response.data.limit) {
        itemsPerPage.value = response.data.limit
        pageCount.value = Math.ceil(commentsCount.value / itemsPerPage.value)
      }
      if(response.data.offset) {
        offset.value = response.data.offset
        pageNum.value = Math.ceil(offset.value / itemsPerPage.value)
      } 
      if(activeTab.value == 'unpublished'){
        responseData.forEach(comment => {
          if(comment.isPublished === false) allComments.value.push(comment)
        })
      }
      else if(activeTab.value == 'published') {
        responseData.forEach(comment => {
          if(comment.isPublished) allComments.value.push(comment)

        })
      }
      allComments.value.sort(
        (a, b) => new Date(b.lastUpdate) - new Date(a.lastUpdate)
      );
    })
    .catch((err) => console.error(err));
};

function handlePublish(commentId) {
  publishComment(baseUrl, commentId, authToken.value)
    .then((response) => {
      allComments.value = allComments.value.filter((c) => c.id !== response.data.id);
    })
    .catch(console.error);
}

function handleUnpublish(commentId) {
  unpublishComment(baseUrl, commentId, authToken.value)
    .then((response) => {
      allComments.value = allComments.value.filter((c) => c.id !== response.data.id);
    })
    .catch(console.error);
}

function handleDelete(commentId) {
  deleteComment(baseUrl, commentId, authToken.value)
    .then((response) => {
      allComments.value = allComments.value.filter((c) => c.id !== response.data.id);
    })
    .catch(console.error);
}

function handleWithdrowingReport(commentId) {
  withdrawReport(baseUrl, commentId, authToken.value)
    .then(() => {
      getComments()
    })
    .catch(console.error);
}

</script>

<style lang="scss" scoped>

.mod-overview {
  min-height: 60vh;
  margin-bottom: 60px !important;
}
.card-header {
  margin-top: 25px;
}

.mod-nav-tabs li .nav-link {
  color: inherit;
  &.active {
    font-weight: bold;
    color: var(--primary-light);
    border-top: 4px solid var(--primary-light);
    margin-bottom: -2px;
  }
  &:hover:not(.active) {
    border-color: transparent;
  }
}

table {
  margin-left: 0px !important;
}
.mod-overview {
  min-height: 60vh;
  margin-bottom: 60px !important;
}

.btn-action {
  border: solid 1px #3f3f3f;
}

.btn-action:hover {
  border: solid 1px #3f3f3f;
  background-color: #3f3f3f;
  color: #fff;
}

.buttonWrapper button {
  margin: 0.2rem;
}

.buttonWrapper {
  width: 100%;
  display: flex;
}

th,
td {
  padding: 1rem;
}

th {
  text-align: center;
}
tr {
  padding: 1rem;
  border-bottom: 1px solid lightgray;
}

thead {
  border-bottom: 1px solid lightgray;
}
</style>
